/**
 * decrypt.ts
 *
 * Utility functions for decrypting strings and data using AES and CryptoJS.
 * Used for secure data handling in the application.
 */
import CryptoJS from "crypto-js";
import logger from "./logger";
const ENCRYPTION_KEY=import.meta.env.VITE_ENCRYPTION_KEY

/**
 * Decrypts a base64-encoded AES-encrypted string using the provided key and IV.
 * @param base64CipherText - The encrypted string in base64
 * @param key - The decryption key
 * @param iv - The initialization vector
 * @returns The decrypted UTF-8 string
 */
export  function decryptStringFromBytes(base64CipherText:string, key:string, iv:string) {
  const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
  const ivUtf8 = CryptoJS.enc.Utf8.parse(iv);
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: CryptoJS.enc.Base64.parse(base64CipherText),
  });

  const decrypted = CryptoJS.AES.decrypt(cipherParams, keyUtf8, {
    iv: ivUtf8,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}

/**
 * Decrypts and parses initialization data from a cipher text string.
 * @param cipherText - The encrypted string
 * @returns The decrypted object or empty object on error
 */
export const decryptInitData = (cipherText: string): Record<string, any> => {
    try {
      const bytes = CryptoJS.AES.decrypt(cipherText, ENCRYPTION_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch (err) {
      logger.error(`${decryptInitData.name} - decryptInitData`, err);
      return {};
    }
  };

/**
 * Decrypts a value using the app's encryption key.
 * @param encrypted - The encrypted value
 * @returns The decrypted object or empty object on error
 */
export const decrypt = (encrypted:any) => {
    if(!encrypted){
        return {};
    }
    let key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
    let iv = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
    let options = {
        keySize: 128 / 8,
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    };
    let decrypted = CryptoJS.AES.decrypt(encrypted, key, options);
    let plaintext = decrypted.toString(CryptoJS.enc.Utf8);
    let data = JSON.parse(plaintext);
    return data
} 

export const getCookie = (name: string) => {
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? match.pop() : '';
};